import { BusinessKeywordsMapping } from 'api/client/mapping/business-keywords-mapping';
import { BusinessTypesMapping } from 'api/client/mapping/business-types-mapping';
import { EssentialTypesMapping } from 'api/client/mapping/essential-types-mapping';
import { HolidayTripData, HolidayTripDetail, HolidayTripDetailApi, HolidayTripPermissions } from 'api/holiday-trip-detail';
import { autoinject } from "aurelia-framework";
import { HolidayMaker, HolidayTrip } from 'model/forms/holiday-trip';
import { AccommodationSegment, CitySegment, NoteSegment, TransportSegment, TravelSegment } from 'model/forms/trip-details';
import { HolidayTripAccommodationSegmentDetailDTO, HolidayTripAccommodationSegmentUpdateDTO, HolidayTripCitySegmentDetailDTO, HolidayTripCitySegmentUpdateDTO, HolidayTripCreateDTO, HolidayTripDetailDTO, HolidayTripDetailModel, HolidayTripNoteSegmentDetailDTO, HolidayTripNoteSegmentUpdateDTO, HolidayTripPermissionsModel, HolidayTripRelatedTravellerUpdateDTO, HolidayTripsClient, HolidayTripSegmentDetailDTO, HolidayTripTransportationSegmentDetailDTO, HolidayTripTransportationSegmentUpdateDTO, HolidayTripTravellerDetailDTO, HolidayTripTravellerUpdateDTO, HolidayTripUpdateDTO } from 'api/swagger/ApiClient';

@autoinject
export class HolidayTripDetailClientApi implements HolidayTripDetailApi {
  constructor(private holidayTripsClient: HolidayTripsClient, private essentialTypesMapping: EssentialTypesMapping, private businessKeywordsMapping: BusinessKeywordsMapping,
    private businessTypesMapping: BusinessTypesMapping) {
  }

  async getHolidayTrip(id: number): Promise<HolidayTripDetail> {
    return this.mapToHolidayTripDetail(await this.holidayTripsClient.getById(id));
  }

  async createHolidayTrip(holidayTrip: HolidayTrip): Promise<HolidayTripDetail> {
    return this.mapToHolidayTripDetail(await this.holidayTripsClient.createHolidayTrip(this.mapToHolidayTripCreateDTO(holidayTrip)));
  }

  async updateHolidayTrip(id: number, holidayTrip: HolidayTrip): Promise<HolidayTripDetail> {
    return this.mapToHolidayTripDetail(await this.holidayTripsClient.updateHolidayTrip(id, this.mapToHolidayTripUpdateDTO(holidayTrip)));
  }

  async archiveHolidayTrip(id: number): Promise<HolidayTripDetail> {
    return this.mapToHolidayTripDetail(await this.holidayTripsClient.archiveHolidayTrip(id));
  }

  async restoreHolidayTrip(id: number): Promise<HolidayTripDetail> {
    return this.mapToHolidayTripDetail(await this.holidayTripsClient.unarchiveHolidayTrip(id));
  }

  private mapToHolidayTripDetail(dto: HolidayTripDetailModel): HolidayTripDetail {
    return {
      data: this.mapToHolidayTripData(dto.holidayTrip),
      permissions: this.mapToHolidayTripPermissions(dto.permissions)
    }
  }

  private mapToHolidayTripData(dto: HolidayTripDetailDTO): HolidayTripData {
    return {
      id: dto.id,
      name: dto.name,
      segments: dto.accommodationSegments.map(x => x as HolidayTripSegmentDetailDTO)
        .concat(dto.citySegments.map(x => x as HolidayTripSegmentDetailDTO))
        .concat(dto.transportationSegments.map(x => x as HolidayTripSegmentDetailDTO))
        .concat(dto.noteSegments.map(x => x as HolidayTripSegmentDetailDTO))
        .sort((a, b) => a.ordinalNumber - b.ordinalNumber)
        .map(x => {
          if (x instanceof HolidayTripAccommodationSegmentDetailDTO)
            return this.mapToAccommodationSegment(x as HolidayTripAccommodationSegmentDetailDTO);
          else if (x instanceof HolidayTripCitySegmentDetailDTO)
            return this.mapToCitySegment(x as HolidayTripCitySegmentDetailDTO);
          else if (x instanceof HolidayTripTransportationSegmentDetailDTO)
            return this.mapToTransportSegment(x as HolidayTripTransportationSegmentDetailDTO);
          else if (x instanceof HolidayTripNoteSegmentDetailDTO)
            return this.mapToNoteSegment(x as HolidayTripNoteSegmentDetailDTO);
          else
            throw new Error('Not supported segment type.');
        }),
      travelers: dto.travellers.map(x => this.mapToHolidayMaker(x)),
      createdBy: this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.createdBy),
      createdOn: dto.createdOn,
      modifiedBy: dto.modifiedBy !== null ? this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.modifiedBy) : null,
      modifiedOn: dto.modifiedOn,
      archivedBy: dto.archivedBy !== null ? this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.archivedBy) : null,
      archivedOn: dto.archivedOn
    }
  }

  private mapToAccommodationSegment(dto: HolidayTripAccommodationSegmentDetailDTO): AccommodationSegment {
    return {
      kind: 'accommodation-segment',
      type: this.businessKeywordsMapping.mapToAccommodationType(dto.accommodationType),
      arrivalDate: dto.arrivalDate,
      departureDate: dto.departureDate,
      name: dto.name,
      address: dto.address,
      city: dto.city !== null ? this.businessKeywordsMapping.mapToCity(dto.city) : null,
      cityName: dto.city !== null ? dto.city.name : dto.cityCustomName,
      country: dto.country !== null ? this.businessKeywordsMapping.mapToCountry(dto.country) : null
    }
  }

  private mapToCitySegment(dto: HolidayTripCitySegmentDetailDTO): CitySegment {
    return {
      kind: 'city-segment',
      arrivalDate: dto.arrivalDate,
      departureDate: dto.departureDate,
      city: this.businessKeywordsMapping.mapToCity(dto.city)
    }
  }

  private mapToTransportSegment(dto: HolidayTripTransportationSegmentDetailDTO): TransportSegment {
    return {
      kind: 'transport-segment',
      type: this.businessKeywordsMapping.mapToTransportType(dto.transportationType),
      departureDate: dto.tripDate,
      airline: dto.airline !== null ? this.businessKeywordsMapping.mapToAirline(dto.airline) : null,
      airlineName: dto.airline !== null ? dto.airline.name : dto.airlineCustomName,
      flightNumber: dto.flightNumber,
      note: dto.note
    }
  }

  private mapToNoteSegment(dto: HolidayTripNoteSegmentDetailDTO): NoteSegment {
    return {
      kind: 'note-segment',
      note: dto.note
    }
  }

  private mapToHolidayMaker(dto: HolidayTripTravellerDetailDTO): HolidayMaker {
    return {
      user: this.businessTypesMapping.mapUserToUserContact(dto.user),
      personalPhone: dto.personalNumber,
      relatedTravelers: dto.relatedTravellers
    }
  }

  private mapToHolidayTripPermissions(dto: HolidayTripPermissionsModel): HolidayTripPermissions {
    return {
      canEdit: dto.canEdit,
      canArchive: dto.canArchive,
      canRestore: dto.canUnarchive
    }
  }

  private mapToHolidayTripCreateDTO(holidayTrip: HolidayTrip): HolidayTripCreateDTO {
    const segmentDTOs = this.getSegmentsDTOs(holidayTrip.segments);
    return new HolidayTripCreateDTO({
      name: holidayTrip.name,
      accommodationSegments: segmentDTOs.filter(x => x instanceof HolidayTripAccommodationSegmentUpdateDTO).map(x => x as HolidayTripAccommodationSegmentUpdateDTO),
      citySegments: segmentDTOs.filter(x => x instanceof HolidayTripCitySegmentUpdateDTO).map(x => x as HolidayTripCitySegmentUpdateDTO),
      transportationSegments: segmentDTOs.filter(x => x instanceof HolidayTripTransportationSegmentUpdateDTO).map(x => x as HolidayTripTransportationSegmentUpdateDTO),
      noteSegments: segmentDTOs.filter(x => x instanceof HolidayTripNoteSegmentUpdateDTO).map(x => x as HolidayTripNoteSegmentUpdateDTO),
      travellers: holidayTrip.travelers.map(x => this.mapToHolidayTripTravellerUpdateDTO(x))
    });
  }

  private mapToHolidayTripUpdateDTO(holidayTrip: HolidayTrip): HolidayTripUpdateDTO {
    const segmentDTOs = this.getSegmentsDTOs(holidayTrip.segments);
    return new HolidayTripUpdateDTO({
      name: holidayTrip.name,
      accommodationSegments: segmentDTOs.filter(x => x instanceof HolidayTripAccommodationSegmentUpdateDTO).map(x => x as HolidayTripAccommodationSegmentUpdateDTO),
      citySegments: segmentDTOs.filter(x => x instanceof HolidayTripCitySegmentUpdateDTO).map(x => x as HolidayTripCitySegmentUpdateDTO),
      transportationSegments: segmentDTOs.filter(x => x instanceof HolidayTripTransportationSegmentUpdateDTO).map(x => x as HolidayTripTransportationSegmentUpdateDTO),
      noteSegments: segmentDTOs.filter(x => x instanceof HolidayTripNoteSegmentUpdateDTO).map(x => x as HolidayTripNoteSegmentUpdateDTO),
      travellers: holidayTrip.travelers.map(x => this.mapToHolidayTripTravellerUpdateDTO(x))
    });
  }

  private getSegmentsDTOs(segments: TravelSegment[]): (HolidayTripAccommodationSegmentUpdateDTO | HolidayTripCitySegmentUpdateDTO | HolidayTripTransportationSegmentUpdateDTO | HolidayTripNoteSegmentUpdateDTO)[] {
    let ordinalNumber = 0;
    return segments.map(x => {
      if (x.kind === 'accommodation-segment')
        return this.mapToHolidayTripAccommodationSegmentUpdateDTO(x as AccommodationSegment, ordinalNumber++);
      else if (x.kind === 'city-segment')
        return this.mapToHolidayTripCitySegmentUpdateDTO(x as CitySegment, ordinalNumber++);
      else if (x.kind === 'transport-segment')
        return this.mapToHolidayTripTransportationSegmentUpdateDTO(x as TransportSegment, ordinalNumber++);
      else if (x.kind === 'note-segment')
        return this.mapToHolidayTripNoteSegmentUpdateDTO(x as NoteSegment, ordinalNumber++);
      else
        throw new Error('Not supported segment type.');
    })
  }

  mapToHolidayTripAccommodationSegmentUpdateDTO(accommodationSegment: AccommodationSegment, ordinalNumber: number): HolidayTripAccommodationSegmentUpdateDTO {
    return new HolidayTripAccommodationSegmentUpdateDTO({
      accommodationTypeId: accommodationSegment.type.id,
      arrivalDate: accommodationSegment.arrivalDate,
      departureDate: accommodationSegment.departureDate,
      name: accommodationSegment.name,
      address: accommodationSegment.address,
      cityId: accommodationSegment.city != null ? accommodationSegment.city.id : null,
      cityCustomName: accommodationSegment.city != null ? accommodationSegment.city.name : accommodationSegment.cityName,
      countryId: accommodationSegment.country != null ? accommodationSegment.country.id : null,
      ordinalNumber: ordinalNumber
    })
  }

  mapToHolidayTripCitySegmentUpdateDTO(citySegment: CitySegment, ordinalNumber: number): HolidayTripCitySegmentUpdateDTO {
    return new HolidayTripCitySegmentUpdateDTO({
      arrivalDate: citySegment.arrivalDate,
      departureDate: citySegment.departureDate,
      cityId: citySegment.city.id,
      ordinalNumber: ordinalNumber
    })
  }

  mapToHolidayTripTransportationSegmentUpdateDTO(transportSegment: TransportSegment, ordinalNumber: number): HolidayTripTransportationSegmentUpdateDTO {
    return new HolidayTripTransportationSegmentUpdateDTO({
      transportationTypeId: transportSegment.type.id,
      tripDate: transportSegment.departureDate,
      airlineId: transportSegment.airline != null ? transportSegment.airline.id : null,
      airlineCustomName: transportSegment.airline != null ? transportSegment.airline.name : transportSegment.airlineName,
      flightNumber: transportSegment.flightNumber,
      note: transportSegment.note,
      ordinalNumber: ordinalNumber
    })
  }

  mapToHolidayTripNoteSegmentUpdateDTO(noteSegment: NoteSegment, ordinalNumber: number): HolidayTripNoteSegmentUpdateDTO {
    return new HolidayTripNoteSegmentUpdateDTO({
      note: noteSegment.note,
      ordinalNumber: ordinalNumber
    })
  }

  mapToHolidayTripTravellerUpdateDTO(holidayMaker: HolidayMaker): HolidayTripTravellerUpdateDTO {
    return new HolidayTripTravellerUpdateDTO({
      userUniqueId: holidayMaker.user.uniqueid,
      personalNumber: holidayMaker.personalPhone,
      relatedTravellers: holidayMaker.relatedTravelers.map(x => new HolidayTripRelatedTravellerUpdateDTO(x))
    })
  }
}
